/**
 * Compact one-line summary per step type — used in compact mode (so the
 * timeline reads as a glance-able outline) and in the right-rail outline
 * (so the user can find a step without expanding it).
 *
 * Returns a structured token array so the renderer can style identifiers
 * (`mono`) without dangerouslySetInnerHTML.
 */
import { TransformStep } from "stores/queryBuilderStore"

export type SummaryToken = { kind: "text" | "mono"; value: string }

const truncate = (s: string, n = 38): string =>
  s.length > n ? s.slice(0, n - 1) + "…" : s

export const stepSummary = (s: TransformStep): SummaryToken[] => {
  const mono = (v: string): SummaryToken => ({
    kind: "mono",
    value: truncate(v),
  })
  const text = (v: string): SummaryToken => ({ kind: "text", value: v })
  const placeholder = "(empty)"
  // Tokens are emitted without surrounding whitespace; the flex container
  // applies a `gap` between them. This avoids whitespace collapsing at
  // flex-item boundaries.
  switch (s.type) {
    case "combine_or": {
      const arrow = s.mode === "into_existing" ? "|=" : "←"
      const n = s.sources.length
      return [
        mono(s.name || placeholder),
        text(arrow),
        text(`${n || "no"} field${n === 1 ? "" : "s"}, any`),
      ]
    }
    case "and_combine": {
      const arrow = s.mode === "into_existing" ? "&=" : "←"
      const n = s.sources.length
      return [
        mono(s.name || placeholder),
        text(arrow),
        text(`${n || "no"} field${n === 1 ? "" : "s"}, all`),
      ]
    }
    case "negate":
      if (s.mode === "in_place") {
        return [text("flip"), mono(s.source || placeholder), text("in place")]
      }
      return [
        mono(s.name || placeholder),
        text("← not"),
        mono(s.source || placeholder),
      ]
    case "cleanup": {
      const fill = s.mode === "zero" ? "0" : s.mode
      return [
        text("fill blanks of"),
        mono(s.source || placeholder),
        text(`with ${fill}`),
      ]
    }
    case "qtable":
      return [text("container"), mono(s.name || placeholder)]
    case "field_decl": {
      const init = s.initialValue.trim()
      return [
        mono(s.name || placeholder),
        text(`${s.fieldType.toLowerCase()}${init ? ` = ${init}` : ""}`),
      ]
    }
    case "append": {
      const n = s.children.length
      return [
        mono(s.parent || placeholder),
        text("←"),
        text(`${n || "no"} field${n === 1 ? "" : "s"}`),
      ]
    }
    case "remove":
      if (s.targets.length === 0) return [text("(no items)")]
      return [
        text("remove"),
        mono(s.targets[0]),
        ...(s.targets.length > 1
          ? [text(`+${s.targets.length - 1} more`)]
          : []),
      ]
    case "conditional_assign": {
      const value =
        s.valueMode === "true"
          ? "true"
          : s.valueMode === "blank"
          ? "blank"
          : truncate(s.valueField || placeholder)
      const valueToken: SummaryToken =
        s.valueMode === "field" ? mono(value) : text(value)
      const condCount = s.conditions.length
      return [
        mono(s.target || placeholder),
        text("="),
        valueToken,
        text(
          condCount === 0
            ? "(always)"
            : `if ${condCount} cond${condCount === 1 ? "" : "s"}`
        ),
      ]
    }
    case "copy_structure":
      return [
        mono(s.target || placeholder),
        text("← copy of"),
        mono(s.source || placeholder),
      ]
    case "loop": {
      const v = s.variable || placeholder
      if (s.iteration.kind === "field") {
        return [
          text("for"),
          mono(v),
          text("in"),
          mono(s.iteration.source || placeholder),
        ]
      }
      const n = s.iteration.values.length
      return [
        text("for"),
        mono(v),
        text(`over ${n || "no"} value${n === 1 ? "" : "s"}`),
      ]
    }
    case "define":
      return [
        mono(s.symbol || placeholder),
        text("="),
        mono(s.value || "(blank)"),
      ]
    case "appendstruct":
      return [
        mono(s.target || placeholder),
        text("← copy shape of"),
        mono(s.source || placeholder),
        ...(s.alias ? [text(`as "${s.alias}"`)] : []),
      ]
    case "calc": {
      const expr = s.expression.trim()
      return [
        mono(s.target || placeholder),
        text("="),
        expr ? mono(expr) : text("(empty)"),
      ]
    }
    case "cat": {
      const n = s.categories.filter(
        (c) => c.label.trim() && c.range.trim()
      ).length
      return [
        mono(s.target || placeholder),
        text(`${n || "no"} categor${n === 1 ? "y" : "ies"} from`),
        mono(s.source || placeholder),
      ]
    }
    case "weighting": {
      if (s.mode === "apply") {
        return [text("apply weights"), mono(s.weightsField || placeholder)]
      }
      const v = s.dimensions.filter((d) => d.container.trim()).length
      return [
        mono(s.destField || placeholder),
        text(`rim weight, ${v || "no"} variable${v === 1 ? "" : "s"}`),
      ]
    }
  }
}
